import { GameRole, RealmTimeZone, RealmIcon } from '@/types/apiServerContract';

export type ValueOf<T> = T[keyof T];

export enum ApplicationConfig {
	WebsiteTitle = 'Titans-League'
}

export enum HomePaths {
	Index = '/',
	News = '/news',
	Profile = '/profile/:user',
	UserProfile = '/user/profile',
	ResetPassword = '/user/password-reset/:token',
	CancelResetPassword = '/user/cancel-password-reset/:token',
	Notifications = '/user/notifications',
	DonateSucess = '/user/donate/success',
	Connect = '/page/connect',
	Armory = '/armory',
	ArmoryRealm = '/armory/:realmId',
        ArmoryCharacters = '/armory/:realmId/characters',
        ArmoryCharacter = '/armory/:realmId/characters/:name',
        ArmoryGuilds = '/armory/:realmId/guilds',
        ArmoryGuild = '/armory/:realmId/guild/:name',
        ArmoryItems = '/armory/:realmId/items',
	OnlinePlayers = '/page/server-status',
	PvPStatistics = '/page/pvp-statistics',
	Changelog = '/page/changelog',
	Vote = '/user/vote',
	Donate = '/user/donate',
	Gravatar = '/user/profile/gravatar',
	Chat = '/user/chat',
	BugTracker = '/bugtracker',
	BugTrackerSearch = '/bugtracker/search',
	BugTrackerCreateIssue = '/bugtracker/issue/create',
	BugTrackerCreateIssueSuccess = '/bugtracker/issue/create/success',
	BugTrackerDetailsPage = '/bugtracker/issue/:id',
	ContentPage = '/page/*'
}

export enum DashboardPaths {
	Index = '/dashboard',
	Home = '/dashboard/home',
	Users = '/dashboard/users',
	Metrics = '/dashboard/metrics',
	Realm = '/dashboard/realm',
	Realms = '/dashboard/realms',
	News = '/dashboard/news',
	Slider = '/dashboard/slider',
	Toast = '/dashboard/toasts',
	Changelog = '/dashboard/changelogs',
	Content = '/dashboard/content',
	LuaWorkspace = '/dashboard/lua-workspace',
	CoreWorkspace = '/dashboard/core-workspace',
	WorldServerConf = '/dashboard/worldserver',
	Modules = '/dashboard/modules'
}

export enum BusConstants {
	OnLogin = 'OnLogin',
	OnLogout = 'OnLogout',
	UpdateAvailable = 'UpdateAvailable',
	LoginRequired = 'LoginRequired',
	Register = 'Register',
	RealmRequired = 'RealmRequired',
	SelectedRealmChanged = 'SelectedRealmChanged',
	ShowProfileMenu = 'ShowProfileMenu',
	SelectedUserChanged = 'SelectedUserChanged',
	ReloadWebsite = 'ReloadWebsite',
	UpdateUserSessions = 'UpdateUserSessions',
	NotificationReceived = 'NotificationReceived',
	GroupChatDisbanded = 'GroupChatDisbanded',
	GeneralErrorEventKey = 'GeneralErrorEventKey',
	ValidationErrorEventKey = 'ValidationErrorEventKey',
	VoteSuccess = 'VoteSuccess',
	PushBuildStatusToClient = 'PushBuildStatusToClient',
	RealmLogHistoryType = 'RealmLogHistoryType'
}

export const MapGameRole = (role: GameRole): keyof typeof GameRole => {
    switch (role) {
    case GameRole.Admin:
    case GameRole.GameMaster:
    case GameRole.Trial:
    case GameRole.Player:
        return GameRole[role] as keyof typeof GameRole;
    default:
        throw new Error(`Unsupported game role type: ${role}`);
    }
};

export enum ModalConstants {
	Login = 'Login',
	Register = 'Register',
	ResetPassword = 'ResetPassword',
	SelectRealm = 'SelectRealm',
	UserModeration = 'UserModeration',
	ChangeWebsitePassword = 'ChangeWebsitePassword',
	ChangeIngamePassword = 'ChangeIngamePassword',
	EditUserProfile = 'EditUserProfile',
	EditIngameAccount = 'EditIngameAccount',
	MultiSelect = 'MultiSelect',
	EditGroupChat = 'EditGroupChat',
	BuildProcessWindow = 'BuildProcessWindow',
	CommitChangesWindow = 'CommitChangesWindow',
	RealmLogHistoryWindow = 'RealmLogHistoryWindow',
	ModuleConfigWindow = 'ModuleConfigWindow'
}

export function isModal(key: string) {
    return Object.keys(ModalConstants).includes(key);
}

export enum StorageConstants {
	UserData = '__storage_user_data',
	Token = '__storage_token_data',
	Account = '__storage_account_data',
	AccountData = '__storage_account_data_data',
	ActiveRealm = '__storage_active_realm_data',
	LastNewsComment = '__storage_last_news_comment'
}

export const MapRealmIcon = (icon: RealmIcon): keyof typeof RealmIcon => {
    switch (icon) {
    case RealmIcon.Normal:
    case RealmIcon.PvP:
    case RealmIcon.RP:
    case RealmIcon.RPPVP:
        return RealmIcon[icon] as keyof typeof RealmIcon;
    default:
        throw new Error(`Unsupported realm icon: ${icon}`);
    }
};

export const MapRealmTimeZone = (zone: RealmTimeZone): keyof typeof RealmTimeZone => {
    switch (zone) {
    case RealmTimeZone.Development:
    case RealmTimeZone.USA:
    case RealmTimeZone.Oceanic:
    case RealmTimeZone.Tournament:
    case RealmTimeZone.English:
    case RealmTimeZone.TestServer:
        return RealmTimeZone[zone] as keyof typeof RealmTimeZone;
    default:
        throw new Error(`Unsupported realm zone: ${zone}`);
    }
};

export enum SignalREvents {
	UpdateOnlineUsers = 'UpdateOnlineUsers',
	ValidateVersion = 'ValidateVersion',
	UpdateUserInformations = 'UpdateUserInformations',
	UpdateUserSessions = 'UpdateUserSessions',
	ForceLogoutUser = 'ForceLogoutUser',
	ForceLogoutAllUsers = 'ForceLogoutAllUsers',
	LogoutUser = 'LogoutUser',
	InitializeCurrentSession = 'InitializeCurrentSession',
	UpdateNewsComments = 'UpdateNewsComments',
	Notification = 'Notification',
	SynchronizeNotifications = 'SynchronizeNotifications',
	UpdateNewsReaction = 'UpdateNewsReaction',
	UpdateNewsCommentReaction = 'UpdateNewsCommentReaction',
	BugTrackerIssueSubmitted = 'BugTrackerIssueSubmitted',
	BugTrackerIssueUpdated = 'BugTrackerIssueUpdated',
	BugTrackerIssueRemoved = 'BugTrackerIssueRemoved',
	BugTrackerIssueDetailsUpdated = 'BugTrackerIssueDetailsUpdated',
	SynchronizeAccountData = 'SynchronizeAccountData',
	Vote = 'Vote',
	PushBuildStatusToClient = 'PushBuildStatusToClient'
}

export type EmojiCode =
	| '<3'
	| '</3'
	| ':D'
	| ':)'
	| ':-)'
	| ';)'
	| ':('
	| ':p'
	| ';p'
	| ':-D'
	| ';-)'
	| ':-('
	| ':-p'
	| ';-p'
	| ':\'('
	| ':O'
	| ':-O'
	| 'xD'
	| '^^'
	| '><'
	| 'thumb-up'
	| 'thump-down';

export type Emoji = '❤️' | '💔' | '😃' | '😉' | '😊' | '☹️' | '😋' | '😜' | '😢' | '👍' | '👎' | '😄' | '😆' | '😯';

export type EmojiMapType = {
	[code in EmojiCode]: Emoji;
};

export const EmojiMap: EmojiMapType = {
    '<3': '❤️',
    '</3': '💔',
    ':D': '😃',
    ':)': '😊',
    ';)': '😉',
    ':(': '☹️',
    ':p': '😋',
    ';p': '😜',
    ':-D': '😃',
    ':-)': '😊',
    ';-)': '😉',
    ':-(': '☹️',
    ':-p': '😋',
    ';-p': '😜',
    ':\'(': '😢',
    xD: '😆',
    'thumb-up': '👍',
    'thump-down': '👎',
    '^^': '😄',
    '><': '😆',
    ':-O': '😯',
    ':O': '😯'
};

export const GetEmoji = (emoji: EmojiCode): Emoji => {
    return EmojiMap[emoji];
};

export enum Faction {
	Alliance = 0,
	Horde = 1,
	All = 2
}

export enum CustomHeaders {
	Token = 'x-titan-token',
	TokenExpired = 'x-token-expired'
}
